/**
 * SEOHead — Iter 191m.
 *
 * Injects vendor-configured SEO tags into <head> for the public storefront.
 * Since this is a client-rendered React app, tags are inserted via
 * `document.head` on mount and removed on unmount so each page's SEO stays
 * clean. Facebook / LinkedIn / X / Slack crawlers all run JS since ~2020, so
 * this works for all major share-preview services. Google itself also renders
 * JS for indexing.
 *
 * Emits:
 *   • <title>
 *   • <meta name="description">
 *   • Open Graph tags (og:title, og:description, og:image, og:url, og:type)
 *   • Twitter card tags
 *   • Site-verification meta tags (google-site-verification, msvalidate.01,
 *     p:domain_verify, yandex-verification)
 *   • <link rel="canonical">
 *   • Optional <meta name="robots" content="noindex,nofollow"> when the vendor
 *     has toggled indexable OFF
 *   • GA4 & Google Tag Manager loader <script> tags (deferred; consent-aware
 *     via CookieConsent visitor decision if present in localStorage)
 *   • JSON-LD Organization + LocalBusiness structured data
 */
import { useEffect } from 'react';

const MANAGED_KEY = 'entrepren-seo-managed';

function upsertTag(tagName, attrs) {
  const selector = tagName + Object.entries(attrs)
    .filter(([k]) => ['name', 'property', 'rel', 'itemprop'].includes(k))
    .map(([k, v]) => `[${k}="${CSS.escape(v)}"]`)
    .join('');
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(tagName);
    el.setAttribute(MANAGED_KEY, '1');
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === '') { el.removeAttribute(k); continue; }
    el.setAttribute(k, String(v));
  }
  return el;
}

function upsertScript(id, src, isBody = false) {
  let el = document.getElementById(id);
  if (el) return el;
  el = document.createElement('script');
  el.id = id;
  el.async = true;
  el.setAttribute(MANAGED_KEY, '1');
  el.src = src;
  if (isBody) document.body.appendChild(el); else document.head.appendChild(el);
  return el;
}

function upsertInlineScript(id, contents, type = 'text/javascript') {
  let el = document.getElementById(id);
  if (el) { el.textContent = contents; return el; }
  el = document.createElement('script');
  el.id = id;
  el.type = type;
  el.setAttribute(MANAGED_KEY, '1');
  el.textContent = contents;
  document.head.appendChild(el);
  return el;
}

function clearManaged() {
  document.head.querySelectorAll(`[${MANAGED_KEY}]`).forEach((el) => el.remove());
  document.body.querySelectorAll(`[${MANAGED_KEY}]`).forEach((el) => el.remove());
}

/** Read the visitor's cookie-consent decision (set by PublicCookieBanner). */
function hasAnalyticsConsent(subdomain) {
  try {
    const raw = localStorage.getItem('entrepren_cookie_consent:' + subdomain);
    if (!raw) return true; // No banner shown or consent not yet made → default allow (opt-out sites)
    const parsed = JSON.parse(raw);
    return parsed?.categories?.analytics !== false;
  } catch { return true; }
}

export default function SEOHead({ site, page, subdomain, url }) {
  useEffect(() => {
    if (!site) return;
    const seo = site.settings?.seo || {};
    const brand = site.brand_name || site.name || 'EntrepreNatives';
    const pageTitle = page?.title || page?.page_id || 'Home';
    const template = seo.title_template || '{page_title} · {site_name}';
    const title = template
      .replace('{page_title}', pageTitle)
      .replace('{site_name}', brand);

    const description = seo.default_meta_description || '';
    const canonical = url || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : '');
    const ogTitle = seo.og_title || title;
    const ogDescription = seo.og_description || description;
    const ogImage = seo.og_image_url || '';

    // <title>
    document.title = title;

    // Standard SEO
    if (description) upsertTag('meta', { name: 'description', content: description });
    if (canonical)   upsertTag('link', { rel: 'canonical', href: canonical });
    upsertTag('meta', {
      name: 'robots',
      content: seo.indexable === false ? 'noindex,nofollow' : 'index,follow',
    });

    // Open Graph
    upsertTag('meta', { property: 'og:title', content: ogTitle });
    if (ogDescription) upsertTag('meta', { property: 'og:description', content: ogDescription });
    if (ogImage)       upsertTag('meta', { property: 'og:image', content: ogImage });
    if (canonical)     upsertTag('meta', { property: 'og:url', content: canonical });
    upsertTag('meta', { property: 'og:type', content: 'website' });
    upsertTag('meta', { property: 'og:site_name', content: brand });

    // Twitter / X card
    upsertTag('meta', { name: 'twitter:card', content: ogImage ? 'summary_large_image' : 'summary' });
    upsertTag('meta', { name: 'twitter:title', content: ogTitle });
    if (ogDescription) upsertTag('meta', { name: 'twitter:description', content: ogDescription });
    if (ogImage)       upsertTag('meta', { name: 'twitter:image', content: ogImage });
    if (seo.twitter_handle) upsertTag('meta', { name: 'twitter:site', content: seo.twitter_handle });

    // Verifications
    if (seo.google_verification)    upsertTag('meta', { name: 'google-site-verification', content: seo.google_verification });
    if (seo.bing_verification)      upsertTag('meta', { name: 'msvalidate.01', content: seo.bing_verification });
    if (seo.pinterest_verification) upsertTag('meta', { name: 'p:domain_verify', content: seo.pinterest_verification });
    if (seo.yandex_verification)    upsertTag('meta', { name: 'yandex-verification', content: seo.yandex_verification });

    // Analytics — only fire when the visitor allows analytics cookies
    const analyticsOk = hasAnalyticsConsent(subdomain || site.subdomain || 'default');
    if (analyticsOk && seo.ga4_measurement_id) {
      upsertScript('ga4-loader', `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(seo.ga4_measurement_id)}`);
      upsertInlineScript('ga4-init', `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', ${JSON.stringify(seo.ga4_measurement_id)});
      `);
    }
    if (analyticsOk && seo.gtm_container_id) {
      upsertInlineScript('gtm-init', `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0], j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer', ${JSON.stringify(seo.gtm_container_id)});
      `);
    }

    // JSON-LD structured data
    const jsonld = [];
    if (seo.schema_organization !== false) {
      const org = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: brand,
        url: canonical ? canonical.replace(/\/$/, '') : undefined,
        logo: site.logo_url || site.settings?.logo_url || undefined,
        sameAs: Object.values(site.social_links || {})
          .filter((v) => typeof v === 'string' && v.startsWith('http')),
      };
      // Drop undefineds
      Object.keys(org).forEach((k) => (org[k] == null || (Array.isArray(org[k]) && !org[k].length)) && delete org[k]);
      jsonld.push(org);
    }
    if (seo.schema_local_business) {
      const biz = {
        '@context': 'https://schema.org',
        '@type': seo.business_type || 'LocalBusiness',
        name: brand,
        telephone: seo.business_phone || undefined,
        address: (seo.business_street || seo.business_city) ? {
          '@type': 'PostalAddress',
          streetAddress: seo.business_street || undefined,
          addressLocality: seo.business_city || undefined,
          addressRegion: seo.business_region || undefined,
          postalCode: seo.business_postal_code || undefined,
          addressCountry: seo.business_country || undefined,
        } : undefined,
      };
      Object.keys(biz).forEach((k) => biz[k] == null && delete biz[k]);
      if (biz.address) Object.keys(biz.address).forEach((k) => biz.address[k] == null && delete biz.address[k]);
      jsonld.push(biz);
    }
    if (jsonld.length) {
      upsertInlineScript('jsonld-schema', JSON.stringify(jsonld.length === 1 ? jsonld[0] : jsonld), 'application/ld+json');
    }

    return () => { clearManaged(); };
  }, [site, page, subdomain, url]);

  return null;
}
